import classNames from 'classnames';
import Image from 'next/image';
import { useLocale } from 'next-intl';
import React, { useId, useMemo } from 'react';
import type { HitsProps } from 'react-instantsearch';
import { useHits } from 'react-instantsearch';

import { IMAGES_ASSETS_PREFIX } from '@/constants';
import { IMAGE_QUALITY_ENTITY_CARD } from '@/constants/image-quality';
import { Link } from '@/i18n/navigation';
import type { Meilisearch_LieuEdition } from '@/lib/api/meilisearch-types';
import { computeLieuPhoto } from '@/lib/utils/lieux/computeLieuPhoto';
import type { PossibleLocales } from '@/utils/AppConfig';

interface LieuEditionCardProps {
  lieu: Meilisearch_LieuEdition;
}
export const LieuEditionCard: React.FC<LieuEditionCardProps> = ({ lieu }) => {
  const locale = useLocale() as PossibleLocales;
  const photo = useMemo(() => computeLieuPhoto(lieu), [lieu]);
  const frName = lieu.nom;
  const enName = lieu.nom_en && lieu.nom_en !== '' ? lieu.nom_en : lieu.nom;
  const name = (locale === 'fr' ? frName : enName) ?? '';

  return (
    <Link
      href={{
        pathname: `/lieux-edition/[slug]`,
        params: { slug: lieu[locale]?.slug },
      }}
      key={`lieu-card-${lieu[locale]?.slug}`}
      className="group card card-compact relative size-full overflow-hidden rounded-md bg-base-100 shadow-sm outline-none transition-all hover:bg-secondary-50 focus:outline-none focus-visible:outline-none active:scale-[.97]"
    >
      <figure className="relative aspect-[1/1] transition-all sm:group-hover:scale-105">
        {photo ? (
          <Image
            // src={`${IMAGES_ASSETS_PREFIX}/${selectionData.miniature_carree}`}
            src={`${IMAGES_ASSETS_PREFIX}/${photo}?fit=cover&width=500&height=500&quality=${IMAGE_QUALITY_ENTITY_CARD}`}
            alt={lieu.nom}
            quality={IMAGE_QUALITY_ENTITY_CARD}
            fill
            style={{
              objectFit: 'cover',
              objectPosition: 'center',
            }}
          />
        ) : (
          <div className="absolute inset-0 bg-gray-200" />
        )}
      </figure>
      <div className="inset-x-0 bottom-0 z-[1] flex h-16 place-content-center items-center bg-white px-3 text-center font-gothic text-gray-700">
        <h5 className="text-center font-gothicb text-sm leading-tight">
          {name}
          {lieu.nom_local && <span> ({lieu.nom_local})</span>}
        </h5>
      </div>
    </Link>
  );
};

interface LieuxEditionHitsProps extends HitsProps<Meilisearch_LieuEdition> {}
export const LieuxEditionHits: React.FC<LieuxEditionHitsProps> = (props) => {
  const { items } = useHits<Meilisearch_LieuEdition>(props);
  const id = useId();

  return (
    <>
      {items.map((hit, idx) => (
        <React.Fragment key={`lieux-${hit.id}-${hit.__queryID}-${id}`}>
          <div
            style={{ '--eo-result-order': `${idx}` } as React.CSSProperties}
            className={classNames(
              'relative order-[var(--eo-result-order)]',
              'col-span-1 border-0',
            )}
          >
            <div
              style={{ display: 'var(--eo-search-scores-visibility, none)' }}
              className="absolute right-0 top-0 z-10 bg-black p-1 text-sm text-white"
            >
              {/* @ts-expect-error */}
              Score : {Number(hit._rankingScore * 100)?.toFixed(2)}
            </div>
            <div>
              <LieuEditionCard lieu={hit} />
            </div>
          </div>
        </React.Fragment>
      ))}
    </>
  );
};
